import { Injectable } from '@angular/core';
import { RESTServiceBase } from '../../common/service.base';
import { List } from '../../common/model/List_T';
import { AppConfig } from '../../configuration/app.config';
import { AuthService } from '../../services/auth.service';
import { NyilvantartasConfiguration } from '../../configuration/configuration.service';
import { ViewConfig } from './model/view';
import { Allapot, Muvelet, MUVELET_FLAG_SZERKESZTOMOD } from './model/statemachine';
import { FormDefinition } from '../../common/dynamic-form/models/form-definition';
import { View } from '../../common/model/view';
import { UniversalContext } from './model/context';
import { CodeListService } from '../codelist/codelist.service';
import { CodeListDescriptor } from '../codelist/model/codelist';
import { IUniversalResolverService, SearchField, SearchItem, UniversalResolver } from '../../common/dynamic-form/universal/universal-resolver';
import { KeresoMezo } from './model/searchfield';
import { version } from 'process';
import { Hatokor } from './model/scope';
import { Observable } from 'rxjs';
import { Dialogus } from './model/dialog';

class GetAvailableTypesRequest {
  public knownVersion: number;
}

class GetAvailableTypesResponse {
  public currentVersion: number = 0;
  public elemTipusok: ElemTipus[];
  public elemCsoportok: ElemCsoport[];
}

export class ElemTipus extends NyilvantartasConfiguration {
  public formId: string;
  public nezetek: ViewConfig[];
  public dialogusok: Dialogus[];
  public allapotok: Allapot[];
  public muveletek: Muvelet[];
  public keresoMezok: KeresoMezo[];
  public hatokorok: Hatokor[];
  public definicio: string;
  
  public formDefinition: FormDefinition;
  public feliratEvaluator: Function;
  public felhasznaloLathatja: boolean;
}

export class ElemCsoport {
  public csoportFelirat: string;
  public csoportIkon: string;
  public felhasznaloLathatja: boolean;

  public elemTipusok: ElemTipus[];
}

class GetItemsRequest extends View {
  public tipus: string;
}

class GetItemsResponse {
  public elemek: any[];
}

class GetDetailsRequest {
  public tipus: string;
  public id: string;
  public verzio: number;
  public alverzio: number;
}

class GetDetailsResponse {
  public meta: any;
}

class GetHistoryRequest {
  public tipus: string;
  public id: string;
  public verzio: number;
  public alverzio: number;
}

class GetHistoryResponse {
  public history: any;
}

class GetItemRequest {
  public tipus: string;
  public id: string;
  public verzio: number;
  public alverzio: number;
  public hivatkozasHatokor: string;
  public hivatkozasMezo: string;
  public hivatkozasErtek: string;
}

class GetItemResponse {
  public adatCsomag: any;
}

class GetIFormRequest {
  public tipus: string;
  public azonosito: string;
}

class GetIFormResponse {
  public statusz: string;
  public adatCsomag: any;
  public iFormAzonositok: string[];
}

class PatchItemRequest {
  public tipus: string;
  public id: string;
  public verzio: number;
  public alverzio: number;
  public muvelet: string;
  public adat: any;
  public megjegyzes: string;
  public datum: Date;
}

class PatchItemResponse {
  public eredmenyElem: any;
}

class GetContextRequest {
  public tipus: string;
  public id: string;
  public verzio: number;
  public alverzio: number;
}

class GetContextResponse {
  public context: UniversalContext;
}

class GetUserPermissionsRequest {
  
}

class GetUserPermissionsResponse {
  public jogosultsagok: any;
}

class GenerateDocumentRequest {
  public tipus: string;
  public id: string;
  public verzio: number;
  public alverzio: number;
  public sablon: string;
}

class RunRequest extends View {
  public tipus: string;
  public muvelet: string;
  public megjegyzes: string;
  public datum: Date;
  public parameterek: any;
  public kijeloles: any;
}

class RunResponse {
  
}

@Injectable()
export class UniversalService extends RESTServiceBase implements IUniversalResolverService {
  private cache: GetAvailableTypesResponse = new GetAvailableTypesResponse();
  private cacheTick: number = 0;
  private jogosultsagok: any;
  private codelists: CodeListDescriptor[];

  constructor(
    private codeListervice: CodeListService,
    private universalResolver: UniversalResolver,
    authService: AuthService,
    config: AppConfig
  ) {
    super(
      'v1',
      'UniversalModule',
      authService,
      config.UNMSConfig
    );

    universalResolver.initialize(this);
  }

  public async isServiceAvailable(): Promise<boolean> {
    return await super.isServiceAvailable();
  }

  public async getItemTypesForMenu(): Promise<any> {
    var config = await this.getAvailableTypes();
    return { elemTipusok: config.elemTipusok, menuCsoportok: config.elemCsoportok };
  }

  public async getItemTypeByName(azonosito: string): Promise<ElemTipus> {
    const nycfg = (await this.getAvailableTypes()).elemTipusok.find(ny => ny.azonosito == azonosito);

    return Promise.resolve(nycfg);
  }

  public async getItemTypeByLabel(felirat: string): Promise<ElemTipus> {
    const nycfg = (await this.getAvailableTypes()).elemTipusok.find(ny => ny.felirat == felirat);

    return Promise.resolve(nycfg);
  }

  public async getItemsByType(tipus: string, view: View): Promise<any> {
    var request = view as GetItemsRequest; 
    request.tipus = tipus;

    return await super.get<GetItemsResponse>(undefined, request, "getitems");
  }

  public async getDetails(tipus: string, id: string, verzio: number, alverzio: number): Promise<any> {
    var request = new GetDetailsRequest(); 
    request.tipus = tipus;
    request.id = id;
    request.verzio = verzio;
    request.alverzio = alverzio;

    var response = await super.get<GetDetailsResponse>(undefined, request, "getdetails");
    return response;
  }

  public async getHistory(tipus: string, id: string, verzio: number, alverzio: number): Promise<any> {
    var request = new GetHistoryRequest(); 
    request.tipus = tipus;
    request.id = id;
    request.verzio = verzio;
    request.alverzio = alverzio;

    var response = await super.get<GetHistoryResponse>(undefined, request, "gethistory");
    return response.history;
  }

  public async getItem(tipus: string, id: string, verzio: number, alverzio: number): Promise<any> {
    var request = new GetItemRequest(); 
    request.tipus = tipus;
    request.id = id;
    request.verzio = verzio;
    request.alverzio = alverzio;

    var response = await super.get<GetItemResponse>(undefined, request, "getitem");
    return response.adatCsomag;
  }

  public async getItemForCreate(tipus: string, id: string, verzio: number, alverzio: number, hivatkozas: any): Promise<any> {
    var request = new GetItemRequest(); 
    request.tipus = tipus;
    request.id = id;
    request.verzio = verzio;
    request.alverzio = alverzio;
    request.hivatkozasMezo = hivatkozas.referenceAlias;
    request.hivatkozasErtek = hivatkozas.referenceId;

    var response = await super.get<GetItemResponse>(undefined, request, "getitem");
    return response.adatCsomag;
  }

  public async getContext(tipus: string, id: string, verzio: number, alverzio: number): Promise<any> {
    var request = new GetContextRequest(); 
    request.tipus = tipus;
    request.id = id;
    request.verzio = verzio;
    request.alverzio = alverzio;

    var response = await super.get<GetContextResponse>(undefined, request, "getcontext");
    return response.context;
  }

  public async getSimpleContext(tipus: string, id: string, verzio: number, alverzio: number): Promise<any> {
    var request = new GetContextRequest(); 
    request.tipus = tipus;
    request.id = id;
    request.verzio = verzio;
    request.alverzio = alverzio;

    var response = await super.get<GetContextResponse>(undefined, request, "getsimplecontext");
    return response.context;
  }

  public async getUserPermissions(): Promise<any> {
    if (!this.jogosultsagok) {
      var request = new GetUserPermissionsRequest(); 
      
      var response = await super.get<GetUserPermissionsResponse>(undefined, request, "getuserpermissions");
      this.jogosultsagok = response.jogosultsagok;
    }

    return this.jogosultsagok;
  }

  public async getIForm(tipus: string, azonosito: string): Promise<GetIFormResponse> {
    var request = new GetIFormRequest(); 
    request.tipus = tipus;
    request.azonosito = azonosito;

    var response = await super.get<GetIFormResponse>(undefined, request, "getiform");
    return response;
  }

  public async patchItem(tipus: string, id: string, verzio: number, alverzio: number, muvelet: string, data: any, comment: string, date: Date): Promise<PatchItemResponse> {
    var request = new PatchItemRequest(); 
    request.tipus = tipus;
    request.id = id || null;
    request.verzio = verzio;
    request.alverzio = alverzio;
    request.muvelet = muvelet;
    request.adat = data;
    request.megjegyzes = comment;
    request.datum = date;

    var response = await super.patch<PatchItemResponse>(undefined, request, "patchitem");
    return response;
  }

  public async getViewDefinitionByType(tipus: string): Promise<ViewConfig> {
    var elemtipus = (await this.getAvailableTypes()).elemTipusok.find(et => et.azonosito == tipus);
    return elemtipus.nezetek ? elemtipus.nezetek[0] : undefined;
  }

  public getCodelists(): CodeListDescriptor[] {
    return this.codelists;
  }

  public generateDocumentAndDownload(tipus: string, id: string, verzio: number, alverzio: number, sablon: string): Promise<Blob> {

    var request = new GenerateDocumentRequest();  
    request.tipus = tipus;
    request.id = id;
    request.verzio = verzio;
    request.alverzio = alverzio;
    request.sablon = sablon;

    return this.auth.download(this.buildUrl(undefined, "generatedocument"), request);
  }

  public import(payload: any, files: Set<File>, doneCallback?: () => void, errorCallback?: (error) => void): { [key: string]: Observable<number> } {
     return this.auth.uploadWithPayload(this.buildUrl(undefined, "import"), payload , files, doneCallback, errorCallback);
  }

  public async run(tipus: string, view: View, muvelet: string, comment: string, date: Date, parameterek: any, selection: any): Promise<RunResponse> {
    var request = view as RunRequest; 
    request.tipus = tipus;
    request.muvelet = muvelet;
    request.megjegyzes = comment;
    request.datum = date;
    request.parameterek = parameterek;
    request.kijeloles = selection;

    var response = await super.post<RunResponse>(request, undefined, "run");
    return response;
  }

  private async getAvailableTypes(): Promise<GetAvailableTypesResponse> {
    if (this.cacheTick && (new Date().valueOf() - 600000 <= this.cacheTick)) return this.cache;

    if (!this.codelists) {
      this.codelists = (await this.codeListervice.getCodeLists()).value;
    }

    var request = new GetAvailableTypesRequest(); 
    request.knownVersion = this.cache.currentVersion;

    try 
    {
      var response = await super.get<GetAvailableTypesResponse>(undefined, request, "getavailabletypes");

      if (response.currentVersion > request.knownVersion) { 
        response.elemTipusok.forEach((elemTipus) => {
          const feliratKifejezes = (elemTipus.feliratKifejezes || "''").split('=>');
          elemTipus.feliratEvaluator = new Function('data', 'return ' + (feliratKifejezes.length > 1 ? feliratKifejezes[1].replace(/elem\./g,'data.') : ''));
          elemTipus.formDefinition = JSON.parse(elemTipus.definicio) as FormDefinition;
          elemTipus.definicio = undefined;
          elemTipus.felhasznaloLathatja = !elemTipus.szerepkor || this.auth.hasBusinessRule(elemTipus.szerepkor);
          elemTipus.utemezettFeladat = elemTipus.utemezettFeladatok && elemTipus.utemezettFeladatok.length > 0;
          if (elemTipus.csoportFelirat) {
            response.elemCsoportok = response.elemCsoportok || [];

            let elemCsoport = response.elemCsoportok.find(ecs => ecs.csoportFelirat == elemTipus.csoportFelirat);

            if (!elemCsoport) {
              elemCsoport = new ElemCsoport();
              elemCsoport.csoportFelirat = elemTipus.csoportFelirat;
              elemCsoport.csoportIkon = elemTipus.csoportIkon;
              elemCsoport.elemTipusok = [];
              response.elemCsoportok.push(elemCsoport);
            }

            elemCsoport.felhasznaloLathatja = elemCsoport.felhasznaloLathatja || elemTipus.felhasznaloLathatja;
            elemCsoport.elemTipusok.push(elemTipus);
          }

          elemTipus.nezetek.forEach(nezet => {
            if (!nezet.muveletek) return;

            nezet.muveletek.forEach(muvelet => {
              muvelet.evaluator = new Function('data','context', 'return ' + (muvelet.condition || 'true'));

              muvelet.buttonclass = 'btn-primary';
              muvelet.buttonicon = '';
              muvelet.icon.split(',').forEach(i => {
                if (i.startsWith('fa')) muvelet.buttonicon = i;
                if (i.startsWith('btn')) muvelet.buttonclass = i;
              });
            });
          });

          if (!elemTipus.muveletek) return;

          elemTipus.muveletek.forEach(muvelet => {
            muvelet.evaluator = new Function('data','context', 'return ' + (muvelet.condition || 'true'));

            muvelet.buttonclass = 'btn-primary';
            muvelet.buttonicon = '';
            muvelet.icon.split(',').forEach(i => {
              if (i.startsWith('fa')) muvelet.buttonicon = i;
              if (i.startsWith('btn')) muvelet.buttonclass = i;
            });
            muvelet.rendermode = muvelet.flags.includes(MUVELET_FLAG_SZERKESZTOMOD) ? 1 : 0;
          });
        });

        this.cache = response; 
      }
    } 
    catch (ex)
    {
      return null;
    }

    this.cacheTick = new Date().valueOf(); 
    return this.cache;
  }

  async getSearchFields(type: string): Promise<SearchField[]> {
    var config = await this.getAvailableTypes();
    var elemtipus = config.elemTipusok.find(et => et.azonosito == type);

    return elemtipus.keresoMezok.map(km => ({ fieldName : km.alias, placeHolder : km.caption, value: '' }));
  }

  async getSearchItems(type: string, valueField: string, filters: SearchField[]): Promise<SearchItem[]> {
    var config = await this.getAvailableTypes();
    var elemtipus = config.elemTipusok.find(et => et.azonosito == type);

    var now = new Date();
    var view = new View();
    view.addFilter("Ervenyesseg", now.toISOString().substring(0, 10));
    view.addFilter("UtolsoAlverzio", 1);
    filters && filters.filter(f => f.value).forEach(f => view.addFilter(f.fieldName, f.value));
    view.Limit = 100;
    var items = await this.getItemsByType(type, view);

    return items.value.map(si => ({ id: si.Id, version: si.Verzio, subversion: si.Alverzio, value: si[valueField], title: elemtipus.feliratEvaluator(si) }));
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/universal/universal.service.ts